import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Search, 
  Plus, 
  Minus,
  Edit2, 
  Trash2, 
  RefreshCw,
  MoreHorizontal,
  Download,
  Headphones,
  CheckCircle,
  Clock,
  AlertTriangle,
  Package,
  MapPin,
  X,
  ShoppingCart
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCHES = {
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela",
  "OTG-A": "Orion On The Go - A",
  "OTG-B": "Orion On The Go - B",
  "OTG-C": "Orion On The Go - C"
};

const ISSUE_TYPES = ["Wrong Bulb Size", "Lacking Item", "Wrong Item", "Defective", "Missing", "Walk-In Replacement", "Incomplete", "Other"];
const ACTIONS = ["Return Received", "Replacement", "Reship", "Refund"];
const STATUSES = ["Pending", "Processing", "Resolved"];

export default function AfterSales() {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [issueFilter, setIssueFilter] = useState("");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states - enhanced with inventory fields
  const [formBranch, setFormBranch] = useState("QC");
  const [formTracking, setFormTracking] = useState("");
  const [formCustomerName, setFormCustomerName] = useState("");
  const [formContactNumber, setFormContactNumber] = useState("");
  const [formIssueType, setFormIssueType] = useState("");
  const [formIssueDescription, setFormIssueDescription] = useState("");
  const [formAction, setFormAction] = useState("");
  const [formNewTracking, setFormNewTracking] = useState("");
  const [formStaff, setFormStaff] = useState("");
  const [formStatus, setFormStatus] = useState("Pending");
  const [formReturnLocation, setFormReturnLocation] = useState("Display Area");
  
  // CART for multiple items
  const [cart, setCart] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [stockCache, setStockCache] = useState({});
  const [branchLocations, setBranchLocations] = useState(["Display Area", "Stock Room"]);
  
  const searchRef = useRef(null);

  // Fetch item masterlist
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/item-master/list`);
      setAllItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  // Fetch branch locations
  const fetchBranchLocations = async () => {
    try {
      const response = await axios.get(`${API}/inventory-tracker/locations/${formBranch}`);
      setBranchLocations(response.data.locations || ["Display Area", "Stock Room"]);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  // Check stock for an item
  const checkStock = async (itemName) => {
    if (!itemName || stockCache[itemName] !== undefined) return stockCache[itemName];
    
    try {
      const response = await axios.get(`${API}/inventory/stock-check/${formBranch}/${encodeURIComponent(itemName)}`);
      const stock = response.data.display_area_stock ?? response.data.total_stock ?? 0;
      setStockCache(prev => ({ ...prev, [itemName]: stock }));
      return stock;
    } catch (error) {
      console.error("Error checking stock:", error);
      return 0;
    }
  };

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (issueFilter && issueFilter !== "all") params.append("issue_type", issueFilter);

      const response = await axios.get(`${API}/after-sales?${params.toString()}`);
      setCases(response.data);
    } catch (error) {
      console.error("Error fetching cases:", error);
      toast.error("Failed to fetch After-Sales cases");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, issueFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/after-sales/stats/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    fetchCases();
    fetchStats();
    fetchItems();
  }, [fetchCases]);

  useEffect(() => {
    fetchBranchLocations();
    setStockCache({});
  }, [formBranch]);

  // Filter items based on search
  useEffect(() => {
    if (!itemSearch) {
      setFilteredItems([]);
    } else {
      const query = itemSearch.toLowerCase();
      const filtered = allItems.filter(item => 
        item.item_name?.toLowerCase().includes(query)
      ).slice(0, 10);
      setFilteredItems(filtered);
    }
  }, [itemSearch, allItems]);

  // Add item to cart
  const addToCart = async (item) => {
    const existingIdx = cart.findIndex(c => c.item_name === item.item_name);
    
    if (existingIdx >= 0) {
      const newCart = [...cart];
      newCart[existingIdx].quantity += 0.5;
      setCart(newCart);
    } else {
      const stock = await checkStock(item.item_name);
      setCart([...cart, {
        ...item,
        quantity: 1,
        available_stock: stock
      }]);
    }
    
    setItemSearch("");
    setFilteredItems([]);
  };

  // Update cart quantity
  const updateCartQuantity = (index, newQty) => {
    if (newQty <= 0) {
      removeFromCart(index);
      return;
    }
    const newCart = [...cart];
    newCart[index].quantity = newQty;
    setCart(newCart);
  };

  // Remove from cart
  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // Clear form
  const clearForm = () => {
    setFormTracking("");
    setFormCustomerName("");
    setFormContactNumber("");
    setFormIssueType("");
    setFormIssueDescription("");
    setFormAction("");
    setFormNewTracking("");
    setFormStaff("");
    setFormStatus("Pending");
    setFormReturnLocation("Display Area");
    setCart([]);
    setItemSearch("");
  };

  // Check if action needs stock deduction
  const needsStockDeduction = (action) => ["Replacement", "Reship"].includes(action);
  const needsStockAddition = (action) => action === "Return Received";

  // Check stock issues
  const hasStockIssues = needsStockDeduction(formAction) && 
    cart.some(item => item.quantity > (item.available_stock || 0));

  // Handle create
  const handleCreate = async () => {
    if (!formTracking || !formCustomerName || cart.length === 0 || 
        !formIssueType || !formAction || !formStaff) {
      toast.error("Please fill in all required fields and add at least one item");
      return;
    }

    if (hasStockIssues) {
      toast.error("Hindi sapat ang stock sa Display Area para sa ilang items");
      return;
    }

    setSaving(true);
    try {
      // Create a case for each item in cart
      const results = [];
      for (const item of cart) {
        const response = await axios.post(`${API}/after-sales`, {
          original_tracking_number: formTracking,
          customer_name: formCustomerName,
          contact_number: formContactNumber,
          item: item.item_name,
          issue_type: formIssueType,
          issue_description: formIssueDescription,
          action_taken: formAction,
          new_tracking_number: formNewTracking,
          staff_in_charge: formStaff,
          status: formStatus,
          branch: formBranch,
          quantity: item.quantity,
          process_inventory: true,
          return_location: formReturnLocation
        });
        results.push(response.data);
      }

      const totalQty = cart.reduce((sum, item) => sum + item.quantity, 0);
      
      if (needsStockDeduction(formAction)) {
        toast.success(`✅ ${cart.length} case(s) created! Stock deducted: -${totalQty} from Display Area`);
      } else if (needsStockAddition(formAction)) {
        toast.success(`✅ ${cart.length} case(s) created! Stock added: +${totalQty} to ${formReturnLocation}`);
      } else {
        toast.success(`✅ ${cart.length} case(s) created!`);
      }
      
      setShowAddDialog(false);
      clearForm();
      await fetchCases();
      await fetchStats();
    } catch (error) {
      console.error("Error creating case:", error);
      toast.error(error.response?.data?.detail || "Failed to create After-Sales case");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingCase) return;

    try {
      await axios.put(`${API}/after-sales/${editingCase.id}`, editingCase);
      toast.success("After-Sales case updated!");
      setShowEditDialog(false);
      setEditingCase(null);
      await fetchCases();
      await fetchStats();
    } catch (error) {
      console.error("Error updating case:", error);
      toast.error("Failed to update case");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this case? Stock changes will NOT be reversed.")) return;

    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API}/after-sales/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Case deleted");
      await fetchCases();
      await fetchStats();
    } catch (error) {
      console.error("Error deleting case:", error);
      toast.error(error.response?.data?.detail || "Failed to delete case");
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (issueFilter) params.append("issue_type", issueFilter);

      const response = await axios.get(`${API}/after-sales/reports/export?${params.toString()}`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `after_sales_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Report exported!");
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />{status}</Badge>;
      case "Processing":
        return <Badge className="bg-blue-100 text-blue-800"><AlertTriangle className="w-3 h-3 mr-1" />{status}</Badge>;
      case "Resolved":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />{status}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getIssueBadge = (issue) => {
    const colors = {
      "Wrong Item": "bg-red-100 text-red-800",
      "Defective": "bg-orange-100 text-orange-800",
      "Missing": "bg-purple-100 text-purple-800",
      "Incomplete": "bg-blue-100 text-blue-800",
      "Other": "bg-gray-100 text-gray-800"
    };
    return <Badge className={colors[issue] || "bg-gray-100 text-gray-800"}>{issue}</Badge>;
  };

  const getActionBadge = (action) => {
    const colors = {
      "Replacement": "bg-orange-100 text-orange-800 border-orange-300",
      "Reship": "bg-blue-100 text-blue-800 border-blue-300",
      "Refund": "bg-green-100 text-green-800 border-green-300",
      "Return Received": "bg-purple-100 text-purple-800 border-purple-300"
    };
    return <Badge variant="outline" className={colors[action] || ""}>{action}</Badge>;
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-gray-50 p-4 space-y-6" data-testid="after-sales-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-lg bg-red-600 flex items-center justify-center">
            <Headphones className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">After-Sales</h1>
            <p className="text-sm text-gray-500">Replacement, Reship, Returns - may inventory movement</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" />
            New Case
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">{stats.total || 0}</div>
                <div className="text-sm text-gray-500">Total Cases</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{stats.pending || 0}</div>
                <div className="text-sm text-gray-500">Pending</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{stats.processing || 0}</div>
                <div className="text-sm text-gray-500">Processing</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{stats.resolved || 0}</div>
                <div className="text-sm text-gray-500">Resolved</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by tracking, customer, or item..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={issueFilter} onValueChange={setIssueFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Issue Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Issues</SelectItem>
                {ISSUE_TYPES.map(i => (
                  <SelectItem key={i} value={i}>{i}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchCases}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cases Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Headphones className="w-5 h-5" />
            After-Sales Cases ({cases.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Headphones className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No After-Sales cases found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tracking #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Issue</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Stock Change</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cases.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-mono text-sm">
                        {c.original_tracking_number}
                        {c.new_tracking_number && (
                          <p className="text-xs text-green-600">New: {c.new_tracking_number}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{c.customer_name}</p>
                        <p className="text-xs text-gray-500">{c.contact_number}</p>
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate" title={c.item}>
                        {c.item}
                      </TableCell>
                      <TableCell className="font-bold">
                        {c.quantity || 1}
                      </TableCell>
                      <TableCell>{getIssueBadge(c.issue_type)}</TableCell>
                      <TableCell>{getActionBadge(c.action_taken)}</TableCell>
                      <TableCell>
                        {c.stock_change ? (
                          <span className={`font-bold ${c.stock_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {c.stock_change > 0 ? '+' : ''}{c.stock_change}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(c.status)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setEditingCase(c);
                              setShowEditDialog(true);
                            }}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(c.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog - Enhanced with Cart */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) clearForm(); setShowAddDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Create After-Sales Case
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Branch Selector */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-blue-800 font-medium">Branch (kung saan babawasan/dadagdagan)</Label>
                  <Select value={formBranch} onValueChange={setFormBranch}>
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(BRANCHES).map(([code, name]) => (
                        <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-blue-800 font-medium">Action Type</Label>
                  <Select value={formAction} onValueChange={setFormAction}>
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue placeholder="Select action" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map(a => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {formAction && (
                <div className="mt-2 text-sm">
                  {needsStockDeduction(formAction) && (
                    <p className="text-orange-700">⚠️ {formAction}: Magbabawas ng stock mula sa Display Area</p>
                  )}
                  {needsStockAddition(formAction) && (
                    <p className="text-green-700">✅ Return Received: Magdadagdag ng stock</p>
                  )}
                  {formAction === "Refund" && (
                    <p className="text-gray-600">ℹ️ Refund: Walang stock movement</p>
                  )}
                </div>
              )}
            </div>

            {/* Return Location (for Return Received) */}
            {needsStockAddition(formAction) && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <Label className="text-green-800 font-medium flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Return Location (Auto-add to DISPLAY)
                </Label>
                <Select value={formReturnLocation} onValueChange={setFormReturnLocation}>
                  <SelectTrigger className="mt-1 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {branchLocations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-green-600 mt-1">
                  ✅ Returned items will be automatically added to {formReturnLocation}
                </p>
              </div>
            )}

            {/* Order Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Original Tracking Number *</Label>
                <Input
                  value={formTracking}
                  onChange={(e) => setFormTracking(e.target.value)}
                  placeholder="JNT..."
                  className="font-mono"
                />
              </div>
              <div>
                <Label>New Tracking # (if replacement/reship)</Label>
                <Input
                  value={formNewTracking}
                  onChange={(e) => setFormNewTracking(e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formCustomerName}
                  onChange={(e) => setFormCustomerName(e.target.value)}
                />
              </div>
              <div>
                <Label>Contact Number</Label>
                <Input
                  value={formContactNumber}
                  onChange={(e) => setFormContactNumber(e.target.value)}
                />
              </div>
            </div>

            {/* Items Cart */}
            <div className="border rounded-lg p-4 space-y-3">
              <Label className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                Items ({cart.length}) - Pwedeng multiple items
              </Label>
              
              {/* Item Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={searchRef}
                  placeholder="Search item to add..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  className="pl-10"
                />
                
                {filteredItems.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredItems.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => addToCart(item)}
                        className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center justify-between border-b last:border-0"
                      >
                        <span className="text-sm">{item.item_name}</span>
                        <Plus className="h-4 w-4 text-red-600" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cart Items */}
              {cart.length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-sm">
                  Search and add items above
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map((item, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-2 rounded border ${
                        needsStockDeduction(formAction) && item.quantity > (item.available_stock || 0)
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="font-medium text-sm">{item.item_name}</div>
                        {needsStockDeduction(formAction) && (
                          <div className="text-xs text-gray-500">
                            Display Stock: {item.available_stock || 0}
                            {item.quantity > (item.available_stock || 0) && (
                              <span className="ml-1 text-red-600">⚠️ Kulang!</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Quantity Controls - 0.5 support */}
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 w-7 p-0"
                          onClick={() => updateCartQuantity(idx, item.quantity - 0.5)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <Input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={item.quantity}
                          onChange={(e) => updateCartQuantity(idx, parseFloat(e.target.value) || 0)}
                          className="w-16 h-7 text-center text-sm font-bold"
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 w-7 p-0"
                          onClick={() => updateCartQuantity(idx, item.quantity + 0.5)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-500"
                          onClick={() => removeFromCart(idx)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  <div className="flex justify-between items-center pt-2 border-t text-sm font-bold">
                    <span>Total Quantity:</span>
                    <span className={needsStockDeduction(formAction) ? 'text-red-600' : 'text-green-600'}>
                      {needsStockDeduction(formAction) ? '-' : '+'}{cartTotal}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Issue Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Type *</Label>
                <Select value={formIssueType} onValueChange={setFormIssueType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select issue" />
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUE_TYPES.map(i => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Staff In Charge *</Label>
                <Input
                  value={formStaff}
                  onChange={(e) => setFormStaff(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label>Issue Description</Label>
              <Textarea
                value={formIssueDescription}
                onChange={(e) => setFormIssueDescription(e.target.value)}
                placeholder="Describe the issue..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { clearForm(); setShowAddDialog(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={saving || cart.length === 0 || hasStockIssues}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? "Saving..." : `Create Case (${cart.length} items)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit After-Sales Case</DialogTitle>
          </DialogHeader>
          {editingCase && (
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select 
                  value={editingCase.status} 
                  onValueChange={(v) => setEditingCase({...editingCase, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>New Tracking Number</Label>
                <Input
                  value={editingCase.new_tracking_number || ""}
                  onChange={(e) => setEditingCase({...editingCase, new_tracking_number: e.target.value})}
                  className="font-mono"
                />
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm"><strong>Item:</strong> {editingCase.item}</p>
                <p className="text-sm"><strong>Quantity:</strong> {editingCase.quantity || 1}</p>
                <p className="text-sm"><strong>Action:</strong> {editingCase.action_taken}</p>
                <p className="text-sm"><strong>Stock Change:</strong> {editingCase.stock_change || 0}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-red-600 hover:bg-red-700">Update Case</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
